import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setTrees,
  setSelectedTree,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addTree,
  updateTree as updateTreeAction,
  removeTree,
  clearSelectedTree,
} from '../../store/slices/decisionTreesSlice';
import {
  GET_DECISION_TREES_LIST,
  GET_DECISION_TREE,
} from '../../graphql/queries/decisionTrees.queries';
import {
  CREATE_DECISION_TREE,
  UPDATE_DECISION_TREE,
  DELETE_DECISION_TREE,
} from '../../graphql/mutations/decisionTrees.mutations';
import {
  FindDecisionTreeListInput,
  FindDecisionTreeInput,
  CreateDecisionTreeInput,
  UpdateDecisionTreeInput,
  DeleteDecisionTreeInput,
  DecisionTreeListResponse,
  DecisionTreeResponse,
  DecisionTreeMutationResponse,
  DeleteDecisionTreeResponse,
  DecisionTree,
} from '../../types/decisionTrees.types';
import ViewDecisionTreeModal from '../../components/DecisionTrees/ViewDecisionTreeModal';
import CreateEditDecisionTreeModal, {
  DecisionTreeFormData,
} from '../../components/DecisionTrees/CreateEditDecisionTreeModal';
import ConfirmDeleteDecisionTreeDialog from '../../components/DecisionTrees/ConfirmDeleteDecisionTreeDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './DecisionTrees.css';

const DecisionTrees: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { trees, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.decisionTrees
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [treeToDelete, setTreeToDelete] = useState<DecisionTree | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchTrees, { loading: fetchingList }] = useLazyQuery<
    { decisionTrees: DecisionTreeListResponse },
    { params: FindDecisionTreeListInput }
  >(GET_DECISION_TREES_LIST, { fetchPolicy: 'network-only' });

  const [fetchTree] = useLazyQuery<
    { decisionTree: DecisionTreeResponse },
    { params: FindDecisionTreeInput }
  >(GET_DECISION_TREE, { fetchPolicy: 'network-only' });

  const [createTreeMutation] = useMutation<
    { createDecisionTree: DecisionTreeMutationResponse },
    { params: CreateDecisionTreeInput }
  >(CREATE_DECISION_TREE);

  const [updateTreeMutation] = useMutation<
    { updateDecisionTree: DecisionTreeMutationResponse },
    { params: UpdateDecisionTreeInput }
  >(UPDATE_DECISION_TREE);

  const [deleteTreeMutation] = useMutation<
    { deleteDecisionTree: DeleteDecisionTreeResponse },
    { params: DeleteDecisionTreeInput }
  >(DELETE_DECISION_TREE);

  useEffect(() => {
    loadTrees();
  }, [pagination.page, pagination.pageSize, search, sort]);

  const loadTrees = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchTrees({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.decisionTrees?.success && data.decisionTrees.data) {
        dispatch(setTrees(data.decisionTrees.data));
      } else {
        dispatch(setError(data?.decisionTrees?.message || 'Error al cargar árboles de decisiones'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'asc' }));
    } else {
      dispatch(setSort({ field: 'displayOrder', order: 'asc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchTree({ variables: { params: { ids: [id] } } });
      if (data?.decisionTree?.success && data.decisionTree.data && data.decisionTree.data.length > 0) {
        dispatch(setSelectedTree(data.decisionTree.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.decisionTree?.message || 'Error al cargar árbol'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchTree({ variables: { params: { ids: [id] } } });
      if (data?.decisionTree?.success && data.decisionTree.data && data.decisionTree.data.length > 0) {
        dispatch(setSelectedTree(data.decisionTree.data[0]));
        setIsEdit(true);
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.decisionTree?.message || 'Error al cargar árbol'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedTree());
    setIsEdit(false);
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: DecisionTreeFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (isEdit && formData.id) {
        const { data: responseData } = await updateTreeMutation({
          variables: {
            params: {
              id: formData.id,
              treeKey: formData.treeKey,
              name: formData.name,
              description: formData.description,
              icon: formData.icon,
              displayOrder: formData.displayOrder,
              isActive: formData.isActive,
            },
          },
        });

        if (responseData?.updateDecisionTree?.success) {
          if (responseData.updateDecisionTree.data) {
            dispatch(updateTreeAction(responseData.updateDecisionTree.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedTree());
          setSaveError(null);
          setSuccessMessage('Árbol de decisiones actualizado exitosamente');
        } else {
          const msg = responseData?.updateDecisionTree?.message || 'Error al actualizar árbol';
          const errs = responseData?.updateDecisionTree?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await createTreeMutation({
          variables: {
            params: {
              treeKey: formData.treeKey,
              name: formData.name,
              description: formData.description,
              icon: formData.icon,
              displayOrder: formData.displayOrder,
              isActive: formData.isActive,
            },
          },
        });

        if (responseData?.createDecisionTree?.success) {
          if (responseData.createDecisionTree.data) {
            dispatch(addTree(responseData.createDecisionTree.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedTree());
          setSaveError(null);
          setSuccessMessage('Árbol de decisiones creado exitosamente');
        } else {
          const msg = responseData?.createDecisionTree?.message || 'Error al crear árbol';
          const errs = responseData?.createDecisionTree?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (tree: DecisionTree) => {
    setTreeToDelete(tree);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!treeToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteTreeMutation({
        variables: {
          params: {
            ids: [treeToDelete.id],
          },
        },
      });

      if (responseData?.deleteDecisionTree?.success) {
        dispatch(removeTree(treeToDelete.id));
        setDeleteDialogOpen(false);
        setTreeToDelete(null);
        setDeleteError(null);
        setSuccessMessage('Árbol de decisiones eliminado exitosamente');
      } else {
        const msg = responseData?.deleteDecisionTree?.message || 'Error al eliminar árbol';
        const errs = responseData?.deleteDecisionTree?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedTree());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedTree());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTreeToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<DecisionTree>[] = [
    {
      field: 'treeKey',
      headerName: 'Clave',
      width: 180,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{ color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/decision-trees/${params.row.treeKey}/nodes`)}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{ color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/decision-trees/${params.row.treeKey}/nodes`)}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string | null) => value || '',
    },
    { field: 'icon', headerName: 'Icono', width: 120 },
    { field: 'displayOrder', headerName: 'Orden', width: 90 },
    {
      field: 'isActive',
      headerName: 'Activo',
      width: 90,
      valueGetter: (value: boolean) => value ? 'Sí' : 'No',
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="decision-trees-container">
        <Paper elevation={2} className="decision-trees-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Árbol de Decisiones</Typography>
          </Breadcrumbs>

          <Box className="decision-trees-header">
            <Typography variant="h4" component="h1">
              Árbol de Decisiones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de árboles de decisiones
            </Typography>
          </Box>

          <Box className="decision-trees-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar árbol..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
              }}
            >
              Agregar Árbol
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={trees}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewDecisionTreeModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditDecisionTreeModal
          open={createEditModalOpen}
          isEdit={isEdit}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteDecisionTreeDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteConfirm}
          treeName={treeToDelete?.name || ''}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default DecisionTrees;
